import { useTranslation } from 'react-i18next';
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
} from '@mui/material';
import { CheckCircle, Cancel, FactCheck } from '@mui/icons-material';
import { RecipeV2Detail } from '../types';

interface ProcessChecksTabProps {
  recipe: RecipeV2Detail;
}

interface ProcessCheck {
  key: string;
  label: string;
  passed: boolean;
  detail: string;
}

/** Derives a rule-based recipe QA checklist purely from fields already on the recipe -
 * no separate mock dataset needed. Distinct from AIInsightsTab, which surfaces AI-generated
 * findings; these are deterministic completeness/validity checks a chef or compliance user
 * can action directly. */
const buildChecks = (recipe: RecipeV2Detail): ProcessCheck[] => {
  const today = new Date().toISOString().slice(0, 10);
  const componentsWithAllergenData = recipe.components.filter((c) => c.allergens && c.allergens.length >= 0);
  const componentsWithCarbonData = recipe.components.filter((c) => c.carbonFactor);

  return [
    {
      key: 'yield',
      label: 'Yield recorded',
      passed: recipe.yieldQuantity > 0 && !!recipe.yieldUom,
      detail: recipe.yieldQuantity > 0 ? `${recipe.yieldQuantity} ${recipe.yieldUom}` : 'Missing yield quantity/UOM',
    },
    {
      key: 'method',
      label: 'Method steps documented',
      passed: recipe.steps.length > 0,
      detail: recipe.steps.length > 0 ? `${recipe.steps.length} step(s) recorded` : 'No method steps recorded',
    },
    {
      key: 'productionMethod',
      label: 'Production/cooking method assigned',
      passed: recipe.productionMethod !== null,
      detail: recipe.productionMethod?.name ?? 'No production method assigned - carbon calc will use default multiplier',
    },
    {
      key: 'allergenData',
      label: 'Ingredient allergen data complete',
      passed: recipe.components.length === 0 || componentsWithAllergenData.length === recipe.components.length,
      detail: `${componentsWithAllergenData.length}/${recipe.components.length} ingredients have allergen data recorded`,
    },
    {
      key: 'carbonData',
      label: 'Ingredient carbon data complete',
      passed: recipe.components.length === 0 || componentsWithCarbonData.length === recipe.components.length,
      detail: `${componentsWithCarbonData.length}/${recipe.components.length} ingredients have a carbon factor`,
    },
    {
      key: 'activeDates',
      label: 'Active date range valid',
      passed: recipe.effectiveFrom <= today && (!recipe.effectiveTo || recipe.effectiveTo >= today),
      detail: recipe.effectiveTo ? `${recipe.effectiveFrom} to ${recipe.effectiveTo}` : `Active from ${recipe.effectiveFrom}, no end date`,
    },
    {
      key: 'costing',
      label: 'Cost per portion calculated',
      passed: recipe.costPerPortion > 0,
      detail: recipe.costPerPortion > 0 ? 'Cost per portion calculated' : 'Cost per portion is zero - check ingredient pricing',
    },
  ];
};

const ProcessChecksTab = ({ recipe }: ProcessChecksTabProps) => {
  const { t } = useTranslation('recipeV2');
  const checks = buildChecks(recipe);
  const failedCount = checks.filter((c) => !c.passed).length;

  return (
    <Stack spacing={3}>
      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Stack direction="row"
          alignItems="center"
          spacing={1}>
          <FactCheck color="action" />
          <Typography variant="textXL">{t('processChecks.title', 'Process Checks')}</Typography>
          <Chip
            label={failedCount === 0 ? t('processChecks.allPassed', 'All checks passed') : t('processChecks.issuesFound', `${failedCount} issue(s) found`)}
            color={failedCount === 0 ? 'success' : 'warning'}
            size="small"
          />
        </Stack>
        <Typography variant="textSM"
          color="text.secondary"
          sx={{ mt: 1 }}>
          {t('processChecks.intro', 'Deterministic completeness/validity checks derived from the recipe data - not AI-generated. See the AI Insights tab for pattern-based findings.')}
        </Typography>
      </Paper>

      <TableContainer component={Paper}
        variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>{t('processChecks.check', 'Check')}</TableCell>
              <TableCell>{t('processChecks.detail', 'Detail')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {checks.map((check) => (
              <TableRow key={check.key}>
                <TableCell sx={{ width: 40 }}>
                  {check.passed ? (
                    <CheckCircle color="success"
                      fontSize="small" />
                  ) : (
                    <Cancel color="warning"
                      fontSize="small" />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="textSM"
                    fontWeight={600}>{check.label}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="textSM"
                    color="text.secondary">{check.detail}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default ProcessChecksTab;
